import {AfterViewInit, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {PaymentDetailsInterface} from '../../models/payment-details.interface';
import {UpdateSelectedProcedure} from '../../store/actions/selected-procedure.actions';
import {Store} from '@ngrx/store';
import {StripeService} from 'ngx-stripe';
import {
  StripeCardElementOptions,
  StripeElements,
  StripeElementsOptions
} from '@stripe/stripe-js';
import {UpdateLoaderVisibility} from '../../store/actions/loader.actions';
import {GetAlertMessage} from '../../store/actions/alert-message.actions';
import {PartnerPaymentService} from '../../services/partner-payment.service';
import {CryptService} from '../../services/crypt.service';
import {LocalStoragePaymentInterface} from '../../models/Local-storage-payment.interface';
import {ConstsHelper} from '../../../../helpers/consts.helper';
import {Location} from '@angular/common';
import {PaymentIntentResponseInterface} from '../../models/payment-intent-response.interface';
import {CustomerInterface} from '../../../account/models/customer.interface';
import {CustomerStorageService} from '../../../account/services/customer-storage.service';
import {AlertMessageInterface} from '../../models/alert-message.interface';
import moment from 'moment';
import {GetMsisdnProcedures} from '../../../lines/store/actions/msisdn-procedures.actions';
import {GetCustomerProcedures} from '../../../lines/store/actions/customer-procedures.actions';
import {GetCards} from '../../../account/store/actions/cards.actions';
import {PartnerSubscriptionSuccess} from '../../store/actions/partner-subscription.actions';
import {environment} from '../../../../../environments/environment';

@Component({
  selector: 'app-payment-form',
  templateUrl: './payment-form.component.html',
  styleUrls: ['./payment-form.component.css'],
  standalone: false
})
export class PaymentFormComponent implements OnInit, AfterViewInit, OnDestroy {

  ConstsHelper = ConstsHelper;

  @Input()
  paymentDetailsInterface: PaymentDetailsInterface;

  localStoragePayment: LocalStoragePaymentInterface;
  localStoragePaymentKey: string;
  customer: CustomerInterface;
  listener: any;
  elements: StripeElements;
  cardOptions: StripeCardElementOptions = {
    style: {
      base: {
        iconColor: '#666EE8',
        color: '#1c2235',
        fontWeight: '300',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSize: '16px',
        '::placeholder': {
          color: '#CFD7E0'
        },
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a'
      }
    }
  };

  elementsOptions: StripeElementsOptions = {
    locale: 'fr'
  };

  constructor(
      private store: Store<any>,
      private stripeService: StripeService,
      private partnerPaymentService: PartnerPaymentService,
      private cryptService: CryptService,
      public location: Location,
      private customerStorageService: CustomerStorageService
  ) {}

  ngOnInit() {
    this.stripeService.setKey(environment.data.stripeToken);
    this.localStoragePayment = null;
    this.localStoragePaymentKey = 'local_storage_payment_' + this.paymentDetailsInterface.type;
    this.customer = this.customerStorageService.getCustomer();
    this.pay(false);
  }

  ngOnDestroy() {
    clearInterval(this.listener);
    this.localStoragePayment = null;
    this.location.replaceState(this.location.path().replace('/debloquer-ligne', '').replace('/crediter-compte', ''));
    if (!(this.location.path().includes('payer-facture') && environment.data.brand !== 'prixtel') || this.paymentDetailsInterface.type === 'refill_or_pass') {
      this.store.dispatch(new UpdateSelectedProcedure(''));
    }
  }

  ngAfterViewInit() {
    this.initForm();
  }

  pay(confirmPayment: boolean) {
    this.store.dispatch(new UpdateLoaderVisibility(1));
    if (localStorage.getItem(this.localStoragePaymentKey)) {
      this.localStoragePayment = JSON.parse(this.cryptService.decrypt(localStorage.getItem(this.localStoragePaymentKey))) as LocalStoragePaymentInterface;
    }
    if (
        this.localStoragePayment &&
        this.localStoragePayment.paymentDetails.amount === this.paymentDetailsInterface.amount &&
        this.localStoragePayment.paymentDetails.procedureCode === this.paymentDetailsInterface.procedureCode &&
        this.localStoragePayment.paymentDetails.type === this.paymentDetailsInterface.type &&
        this.localStoragePayment.paymentDetails.externalId === this.paymentDetailsInterface.externalId &&
        this.localStoragePayment.paymentIntentResponse.paymentIntent.customer === this.customer.id
    ) {
      if (confirmPayment) {
        this.confirmAndSavePayment(this.localStoragePayment.paymentIntentResponse, this.paymentDetailsInterface.type);
      } else {
        this.store.dispatch(new UpdateLoaderVisibility(-1));
      }
    } else {
      const paymentContentBody = {amount: Math.ceil(this.paymentDetailsInterface.amount * 100), type: this.paymentDetailsInterface.type, externalId: this.paymentDetailsInterface.externalId};
      // Call internal stripe API to create payment intent
      this.partnerPaymentService.createPaymentIntent(paymentContentBody).subscribe(paymentIntentResponse => {
        this.localStoragePayment = {paymentIntentResponse, paymentDetails: this.paymentDetailsInterface} as LocalStoragePaymentInterface;
        localStorage.setItem(this.localStoragePaymentKey, this.cryptService.encrypt(JSON.stringify(this.localStoragePayment)));
        this.partnerPaymentService.checkBeforePayment(paymentIntentResponse.paymentIntent.id).subscribe(
            checkResponse => {
              this.store.dispatch(new UpdateLoaderVisibility(-1));
            }, error => {
              this.store.dispatch(new UpdateLoaderVisibility(-1));
              this.store.dispatch(new GetAlertMessage({message: ConstsHelper.ERROR_OCCURRED_RETRY_MESSAGE, code: 400}));
              this.cancel();
            }
        );
      }, error => {
        this.store.dispatch(new UpdateLoaderVisibility(-1));
        this.store.dispatch(new GetAlertMessage({message: ConstsHelper.ERROR_OCCURRED_RETRY_MESSAGE, code: 400}));
        this.cancel();
      });
    }
    this.executeListener();
  }

  private executeListener() {
    this.listener = setInterval(function() {
      if (this.localStoragePayment && this.localStoragePayment.paymentIntentResponse && this.localStoragePayment.paymentIntentResponse.paymentIntent && this.localStoragePayment.paymentIntentResponse.paymentIntent.client_secret) {
        // check payment intent status
        this.stripeService.retrievePaymentIntent(this.localStoragePayment.paymentIntentResponse.paymentIntent.client_secret).subscribe(stripePaymentIntentResponse => {
          if (stripePaymentIntentResponse.paymentIntent.status === 'succeeded') {
            localStorage.removeItem(this.localStoragePaymentKey);
            this.store.dispatch(new UpdateLoaderVisibility(-1));
            this.store.dispatch(new UpdateSelectedProcedure(''));
            this.displayConfirmationMessage();
          }
        });
      }
    }.bind(this), 3000);
  }


  initForm() {
    this.stripeService.elements(this.elementsOptions).subscribe(elements => {
      // tslint:disable-next-line:only-arrow-functions
      elements.create('cardNumber', this.cardOptions).on('change', function(event) {
        const displayError = document.getElementById('invalid_number');
        if (event.error) {
          displayError.innerText = event.error.message;
          displayError.style.display = 'block';
        } else {
          displayError.style.display = 'none';
        }
      }).mount('#card-number');
      // tslint:disable-next-line:only-arrow-functions
      elements.create('cardExpiry', this.cardOptions).on('change', function(event) {
        const displayError = document.getElementById('invalid_expiry_year_past');
        if (event.error) {
          displayError.innerText = event.error.message;
          displayError.style.display = 'block';
        } else {
          displayError.style.display = 'none';
        }
      }).mount('#card-expiry');
      // tslint:disable-next-line:only-arrow-functions
      elements.create('cardCvc', this.cardOptions).on('change', function(event) {
        const displayError = document.getElementById('incomplete_cvc');
        if (event.error) {
          displayError.innerText = event.error.message;
          displayError.style.display = 'block';
        } else {
          displayError.style.display = 'none';
        }
      }).mount('#card-cvc');
      this.elements = elements;
    });
  }

  cancel() {
    localStorage.removeItem(this.localStoragePaymentKey);
    this.ngOnDestroy();
  }

  /**
   * Confirm payment intent and save data in DB
   * @param paymentIntentResponse
   * @param paymentType
   */
  confirmAndSavePayment(paymentIntentResponse: PaymentIntentResponseInterface, paymentType: string) {
    this.store.dispatch(new UpdateLoaderVisibility(1));
    this.partnerPaymentService.checkBeforePayment(paymentIntentResponse.paymentIntent.id).subscribe(
        checkResponse => {
          this.store.dispatch(new UpdateLoaderVisibility(1));
          // Call stripe API to confirm payment
          this.stripeService.confirmCardPayment(paymentIntentResponse.paymentIntent.client_secret, { payment_method: { card: this.elements.getElement('cardNumber')}, return_url: window.location.href }).subscribe(confirmCardPaymentResponse => {
            this.store.dispatch(new UpdateLoaderVisibility(1));
            // Error case : stop loader & display error message
            if (confirmCardPaymentResponse.error) {
              this.displayPaymentError();
            } else if (confirmCardPaymentResponse.paymentIntent && confirmCardPaymentResponse.paymentIntent.status === 'succeeded') {
              this.store.dispatch(new UpdateLoaderVisibility(-1));
            } else {
              this.displayPaymentError();
            }
          }, error => {
            this.displayPaymentError();
          });
        }, error => {
          this.store.dispatch(new UpdateLoaderVisibility(-1));
          this.store.dispatch(new GetAlertMessage({message: ConstsHelper.ERROR_OCCURRED_RETRY_MESSAGE, code: 400}));
          this.cancel();
        }
    );
  }

  cancelButtonExcludedProcedure() {
    if (environment.data.brand == 'prixtel') {
      return [ConstsHelper.PROCEDURE_PARTNER_SUBSCRIPTION];
    } else {
      return [ConstsHelper.PROCEDURE_PARTNER_SUBSCRIPTION, ConstsHelper.PROCEDURE_EXPRESS_PAYMENT];
    }
  }

  private displayConfirmationMessage() {
    const alertMessage = { message: null , code: 200} as AlertMessageInterface;
    const dateValue = parseInt(moment(new Date()).format('YYYYMMDDHHmm'), 10);
    this.store.dispatch(new UpdateLoaderVisibility(-1));
    switch (this.localStoragePayment.paymentDetails.type) {
      case 'nouvelle_carte':
        localStorage.setItem(ConstsHelper.LOCAL_STORAGE_TEMPORARILY_BLOCK_CREATION_NEW_CARD + '_' + this.customer.id, JSON.stringify({date: dateValue}));
        this.store.dispatch(new GetCustomerProcedures('account', null));
        this.store.dispatch(new GetCards());
        alertMessage.message = ConstsHelper.CONFIRMATION_MESSAGE_SAVE_PAYMENT_CARD;
        break;
      case 'credit_compte':
        alertMessage.message = ConstsHelper.CONFIRMATION_MESSAGE_CREDIT_ACCOUNT;
        break;
      case 'partner_subscription':
        this.store.dispatch(new PartnerSubscriptionSuccess());
        break;
      case 'express':
        localStorage.setItem(ConstsHelper.LOCAL_STORAGE_TEMPORARILY_BLOCK_EXPRESS_PAYMENT_PROCEDURE + '_' + this.customer.id, JSON.stringify({date: dateValue}));
        this.store.dispatch(new GetCustomerProcedures('bill', null));
        alertMessage.message = ConstsHelper.CONFIRMATION_MESSAGE_BILL_PAYMENT;
        break;
      case 'deblocage':
        localStorage.setItem(ConstsHelper.LOCAL_STORAGE_TEMPORARILY_BLOCK_UNBLOCK_LINE_PROCEDURE + '_' + this.localStoragePayment.paymentDetails.selectedLine.phoneNumber, JSON.stringify({date: dateValue}));
        this.store.dispatch(new GetMsisdnProcedures(this.localStoragePayment.paymentDetails.selectedLine));
        alertMessage.message = ConstsHelper.CONFIRMATION_MESSAGE_UNBLOCK_LINE;
        break;
      case 'refill_or_pass':
        localStorage.setItem(ConstsHelper.LOCAL_STORAGE_TEMPORARILY_BLOCK_REFILL_OR_PASS_PROCEDURE + '_' + this.localStoragePayment.paymentDetails.selectedLine.phoneNumber, JSON.stringify({date: dateValue}));
        this.store.dispatch(new GetMsisdnProcedures(this.localStoragePayment.paymentDetails.selectedLine));
        alertMessage.message = ConstsHelper.CONFIRMATION_MESSAGE_REFILL_OR_PASS;
        break;
    }
    if (alertMessage.message) {
      this.store.dispatch(new GetAlertMessage(alertMessage));
    }
    this.cancel();
  }

  private displayPaymentError() {
    this.initForm();
    this.store.dispatch(new UpdateLoaderVisibility(-1));
    this.store.dispatch(new GetAlertMessage({message: this.paymentDetailsInterface.errorMessage, code: 400}));
  }
}
